import { useEffect, useState } from "react";
import { documentsService } from "../services/documents";
import { ApiError, getAccessToken } from "../services/api";
import MarkdownEditor from "./MarkdownEditor";
import ChatPanel from "./ChatPanel";
import ModificationHistory from "./ModificationHistory";
import { Button } from "./ui/Button";
import { DocumentSkeleton } from "./ui/Skeleton";
import { useKeyboardShortcuts } from "../hooks/useKeyboardShortcuts";
import { useToast } from "../context/ToastContext";
import type { DocumentType, DocumentVersion } from "../types";

interface Props {
  jobId: string;
  docType: DocumentType;
  label: string;
  isGenerating?: boolean;
}

export default function DocumentEditor({ jobId, docType, label, isGenerating = false }: Props) {
  const { show: showToast } = useToast();
  const [version, setVersion] = useState<DocumentVersion | null>(null);
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"edit" | "chat" | "history">("edit");
  const [dirty, setDirty] = useState(false);

  useKeyboardShortcuts({
    onSave: () => { if (dirty && !saving) handleSave(); },
    isGenerating,
  });

  useEffect(() => {
    loadDocument();
  }, [jobId, docType]);

  async function loadDocument() {
    setLoading(true);
    try {
      const v = await documentsService.getLatest(jobId, docType);
      setVersion(v);
      setContent(v.content);
      setDirty(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setVersion(null);
        setContent("");
      } else {
        showToast("error", "Failed to load document.");
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      const v = await documentsService.save(jobId, docType, content);
      setVersion(v);
      setDirty(false);
      showToast("success", "Document saved.");
    } catch {
      showToast("error", "Failed to save document.");
    } finally {
      setSaving(false);
    }
  }

  function handleContentChange(val: string) {
    setContent(val);
    setDirty(true);
  }

  function handleVersionSelect(v: DocumentVersion) {
    setVersion(v);
    setContent(v.content);
    setDirty(false);
    setTab("edit");
  }

  function handleExportPdf() {
    const token = getAccessToken();
    const url = documentsService.exportPdfUrl(jobId, docType);
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((res) => {
        if (!res.ok) throw new Error("Export failed");
        return res.blob();
      })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = `${docType}-document.pdf`;
        a.click();
        URL.revokeObjectURL(blobUrl);
      })
      .catch(() => showToast("error", "Failed to export PDF."));
  }

  if (loading) {
    return <DocumentSkeleton className="p-6" />;
  }

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 m-0">
          {label}
        </h3>
        <div className="flex items-center gap-2">
          {version && (
            <span className="text-xs text-slate-400 self-center">
              v{version.sequence_number}
            </span>
          )}
          <Button
            onClick={handleSave}
            disabled={saving || !dirty || isGenerating}
            loading={saving}
            variant={dirty ? "primary" : "secondary"}
            size="sm"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
          {version && (
            <Button
              onClick={handleExportPdf}
              disabled={isGenerating}
              variant="ghost"
              size="sm"
            >
              Export PDF
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 dark:border-slate-700">
        {(["edit", "chat", "history"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={[
              "px-5 py-2.5 text-sm border-b-2 transition-colors duration-200 bg-transparent cursor-pointer",
              tab === t
                ? "border-accent text-accent font-semibold"
                : "border-transparent text-slate-500 dark:text-slate-400 font-normal hover:text-slate-800 dark:hover:text-slate-200",
            ].join(" ")}
          >
            {t === "chat" ? "AI Refine" : t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="p-4">
        {tab === "edit" && (
          <MarkdownEditor
            value={content}
            onChange={handleContentChange}
            isGenerating={isGenerating}
          />
        )}
        {tab === "chat" && version && (
          <ChatPanel
            jobId={jobId}
            docType={docType}
            isGenerating={isGenerating}
            onNewVersion={(v) => {
              setVersion(v);
              setContent(v.content);
              setDirty(false);
            }}
          />
        )}
        {tab === "chat" && !version && (
          <p className="text-slate-400 text-sm">
            Generate or save a document first to use AI refinement.
          </p>
        )}
        {tab === "history" && (
          <ModificationHistory
            jobId={jobId}
            docType={docType}
            currentVersionId={version?.version_id}
            onRestore={handleVersionSelect}
          />
        )}
      </div>
    </div>
  );
}
